import React from "react";
import { WORKSHOPS, WHATSAPP_URL, REGISTER_URL, COHORT_INFO } from "@/lib/site";
import { CalendarDays, Clock, MessageCircle, ArrowRight, Sparkles } from "lucide-react";

export default function Schedule() {
    return (
        <section
            id="schedule"
            data-testid="schedule-section"
            className="py-20 md:py-24 bg-[color:var(--navy)] text-white relative overflow-hidden"
        >
            <div
                className="absolute inset-0 pointer-events-none opacity-[0.14]"
                style={{
                    backgroundImage:
                        "radial-gradient(circle at 20% 20%, rgba(181,138,43,0.45), transparent 40%), radial-gradient(circle at 85% 85%, rgba(122,31,47,0.55), transparent 42%)",
                }}
            />
            <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Compact header */}
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
                    <div>
                        <div
                            data-testid="monthly-cohort-badge"
                            className="inline-flex items-center gap-2 rounded-full border border-[color:var(--gold)]/50 bg-[color:var(--gold)]/10 px-3.5 py-1.5 font-mono-label text-[color:var(--gold-soft)]"
                        >
                            <Sparkles className="h-3 w-3" />
                            {COHORT_INFO.cohortType} Available
                        </div>
                        <h2 className="mt-4 font-serif text-3xl sm:text-4xl tracking-tight leading-tight" style={{ fontWeight: 500 }}>
                            {COHORT_INFO.frequency}, {COHORT_INFO.time}
                            <span className="block italic text-white/70 mt-1">
                                {COHORT_INFO.timezone} · {COHORT_INFO.mode}
                            </span>
                        </h2>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                        <a
                            href={REGISTER_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="schedule-register-cohort-btn"
                            className="btn-gold inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm"
                        >
                            Register for Next Cohort
                            <ArrowRight className="h-4 w-4" />
                        </a>
                        <a
                            href={WHATSAPP_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="schedule-whatsapp-btn"
                            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 px-5 py-2.5 text-sm font-semibold hover:bg-white/5 transition-colors"
                        >
                            <MessageCircle className="h-4 w-4" />
                            Ask about next cohort
                        </a>
                    </div>
                </div>

                {/* Compact next-cohort card */}
                <div
                    data-testid="next-cohort-card"
                    className="rounded-3xl border border-[color:var(--gold)]/30 bg-white/[0.04] p-5 sm:p-6"
                >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-white/10 pb-4 mb-2">
                        <div className="flex items-baseline gap-3 flex-wrap">
                            <div className="font-mono-label text-[color:var(--gold-soft)]">
                                Next Cohort
                            </div>
                            <div className="font-serif text-xl sm:text-2xl leading-tight">
                                {COHORT_INFO.cohortLabel}, {COHORT_INFO.cohortMonth}
                            </div>
                        </div>
                        <span className="inline-flex items-center gap-2 rounded-full bg-[color:var(--gold)] text-[color:var(--navy)] px-3 py-1 font-mono-label shadow-sm self-start sm:self-auto">
                            <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--navy)] pulse-dot" />
                            {COHORT_INFO.registrationStatus}
                        </span>
                    </div>

                    <ul className="divide-y divide-white/10">
                        {WORKSHOPS.map((w, i) => (
                            <li
                                key={w.n}
                                data-testid={`schedule-row-${i + 1}`}
                                className="grid grid-cols-12 gap-3 py-3 items-center"
                            >
                                <div className="col-span-2 sm:col-span-1 font-serif text-xl text-[color:var(--gold-soft)]">
                                    {w.n}
                                </div>
                                <div className="col-span-10 sm:col-span-7">
                                    <div className="font-serif text-base leading-tight">
                                        {w.title}
                                    </div>
                                </div>
                                <div className="col-span-12 sm:col-span-4 flex flex-wrap gap-1.5 sm:justify-end">
                                    <span className="inline-flex items-center gap-1.5 rounded-full bg-white/8 border border-white/10 px-2.5 py-1 text-[11px] text-white/80">
                                        <CalendarDays className="h-3 w-3 text-[color:var(--gold-soft)]" />
                                        {w.date}
                                    </span>
                                    <span className="inline-flex items-center gap-1.5 rounded-full bg-white/8 border border-white/10 px-2.5 py-1 text-[11px] text-white/80">
                                        <Clock className="h-3 w-3 text-[color:var(--gold-soft)]" />
                                        {w.time}
                                    </span>
                                </div>
                            </li>
                        ))}
                    </ul>

                    <div
                        data-testid="schedule-future-note"
                        className="mt-4 rounded-xl border border-dashed border-white/15 bg-white/[0.02] px-4 py-3 text-xs text-white/55 text-center"
                    >
                        {COHORT_INFO.futureNote}
                    </div>
                </div>
            </div>
        </section>
    );
}
